import { useCallback, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'

import type {
  AcknowledgmentSignerPickerIntent,
  AcknowledgmentSignerSelectionPayload,
} from '../../model/types'
import { SIGNER_PICKER_TRANSLATION_KEYS as K } from '../../translations'
import { ParticipationField } from '../participationField'

import styles from './SignerAddButton.module.scss'

export const SignerAddButton = ({
  signer,
  intent,
  onSelect,
  canAddOptional = false,
}: {
  signer: AcknowledgmentSignerSelectionPayload
  canAddOptional?: boolean
  intent: AcknowledgmentSignerPickerIntent
  onSelect: (payload: AcknowledgmentSignerSelectionPayload) => void
}) => {
  const t = useSafetyTranslation()
  const [isOptional, setIsOptional] = useState(0)
  const [open, setOpen] = useState(false)
  const focusParticipation = useCallback((node: HTMLDivElement | null) => {
    node?.querySelector<HTMLInputElement>('input[type="radio"]')?.focus()
  }, [])
  const eventCallbacks = useMemo(
    () => ({
      onOpen: () => {
        setIsOptional(0)
        setOpen(true)
      },
      onClose: () => setOpen(false),
    }),
    []
  )
  const fullName = [signer.firstName, signer.lastName].filter(Boolean).join(' ')

  if (intent !== 'add') {
    return (
      <Button variant="outlined" onClick={() => onSelect(signer)}>
        Select
      </Button>
    )
  }

  return (
    <AbsolutePopup
      verticalStartPosition={TPopupVertical.Center}
      verticalDirection={TPopupVertical.Center}
      horizontalStartPosition={TPopupHorizontal.Left}
      horizontalDirection={TPopupHorizontal.Left}
      horizontalOffset={8}
      eventCallbacks={eventCallbacks}
      popupContent={({ action }) => (
        <PopPaper className={styles.participationPopup}>
          <div
            className={styles.participationPopupContent}
            ref={focusParticipation}
            role="dialog"
            aria-label={t(K.addSigner).replaceAll('{signer}', fullName || signer.email)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation()
                action.close()
                action.focus()
              }
            }}
          >
            <div className={styles.participationContact}>
              <h3 className={styles.optionName}>{fullName}</h3>
              <div className={styles.optionEmail}>{signer.email}</div>
            </div>
            <ParticipationField
              value={canAddOptional ? isOptional : 0}
              onChange={setIsOptional}
              optionalDisabled={!canAddOptional}
            />
            <div className={styles.buttonLine}>
              <Button
                className={styles.participationCancel}
                variant="outlined"
                onClick={() => {
                  action.close()
                  action.focus()
                }}
              >
                <Translation text={K.cancel} />
              </Button>
              <Button
                onClick={() => {
                  action.close()
                  onSelect({ ...signer, isOptional: canAddOptional ? isOptional : 0 })
                }}
              >
                <Translation text={canAddOptional && isOptional ? K.addOptional : K.addMandatory} />
              </Button>
            </div>
          </div>
        </PopPaper>
      )}
    >
      <span aria-haspopup="dialog" aria-expanded={open}>
        <Button
          variant="text"
          iconButton="kbase kbase-add"
          title={t(K.addSigner).replaceAll('{signer}', fullName || signer.email)}
          disabled={!signer.email}
        />
      </span>
    </AbsolutePopup>
  )
}
